import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { useForm } from "react-hook-form";
import { useQueryClient } from "@tanstack/react-query";
import { Input } from "@jhool-io/fe-components";
import useToast from "../../../../hooks/useToast";
import {
    IEditUserNamePayload,
    IInternalUser,
} from "../../types/user-management.types";
import { useEditUserName } from "../../hooks/user-management.mutations";

interface EditUserNameFormProps {
    user: IInternalUser;
    onFormSubmit: () => void;
}

const schema = yup.object().shape({
    first_name: yup.string().trim().required("First name is required"),
    last_name: yup.string().trim().required("Last name is required"),
});

export default function EditUserNameForm({
    user,
    onFormSubmit,
}: EditUserNameFormProps) {
    const {
        handleSubmit,
        register,
        formState: { errors },
    } = useForm<IEditUserNamePayload>({
        resolver: yupResolver(schema),
        defaultValues: {
            first_name: user.first_name,
            last_name: user.last_name,
        },
    });

    const editUserName = useEditUserName(user.user_id);

    const queryClient = useQueryClient();

    const { toast } = useToast();

    const onSubmit = (payload: IEditUserNamePayload) => {
        editUserName.mutate(payload, {
            onSuccess: (res) => {
                queryClient.invalidateQueries({ queryKey: ["users"] });
                toast({
                    mode: "success",
                    message: res.message || "User name updated successfully",
                });
                onFormSubmit();
            },
            onError: (err) => {
                toast({
                    mode: "error",
                    message:
                        err.response?.data.message ||
                        "Could not update user name at this time",
                });
            },
        });
    };

    return (
        <form
            id="edit-user-name"
            name="edit_user_name"
            aria-label="edit_user_name"
            onSubmit={handleSubmit(onSubmit)}
        >
            <div className="fg fg-space-between two flex">
                <Input
                    {...register("first_name")}
                    label="First name"
                    placeholder="First name"
                    hasError={!!errors.first_name}
                    errorText={errors.first_name?.message}
                />
                <Input
                    {...register("last_name")}
                    label="Last name"
                    placeholder="Last name"
                    hasError={!!errors.last_name}
                    errorText={errors.last_name?.message}
                />
            </div>
        </form>
    );
}
